import { type AnalyticsDailyRow } from '@carsfinders/contracts';
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';

import { t } from '../../../lib/i18n';
import { formatDayShort, pluralCount } from '../format';

/**
 * Bars = sessions, line = unique visitors, one column per Kyiv day.
 *
 * Plain SVG, no chart dependency (phase 5 rule). The viewBox is the MEASURED
 * width of the container (ResizeObserver), so one viewBox unit is one CSS
 * pixel: 11-unit labels are 11px on a 390px phone as on a 1440px desktop, and
 * the number of x labels follows the width instead of being squeezed.
 *
 * The bars are the ONLY `<rect>`s in the drawing (the grid is `<line>`s), so
 * «N days → N rects» stays a true statement about the picture.
 *
 * Keyboard: the chart is ONE tab stop. Focus selects the latest day, ←/→
 * (Home/End) move between days, and the selected day's numbers are announced
 * through a polite live region the chart is `aria-describedby`. The
 * `<details>` table below carries the same numbers for anyone the picture does
 * not serve.
 */

/** Width used until (or where) the container cannot be measured — e.g. jsdom. */
const DEFAULT_WIDTH = 720;
const MIN_WIDTH = 280;
const NARROW = 520;
const PAD_LEFT = 34;
const PAD_RIGHT = 8;
const PAD_TOP = 10;
const PAD_BOTTOM = 26;
const GRID_LINES = 4;
const FONT = 11;
/** Minimum room per x label, in px — «30 вер» at 11px mono is ~45px. */
const LABEL_SLOT_PX = 64;

/** A y-axis ceiling divisible into four whole steps. */
export function chartCeiling(max: number): number {
  if (max <= GRID_LINES) return GRID_LINES;
  const rough = max / GRID_LINES;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? rough;
  return Math.ceil(step) * GRID_LINES;
}

/** «13 жовт: 42 заходи · 31 унікальний · 9 зареєстрованих». */
export function dayTooltip(row: AnalyticsDailyRow): string {
  return `${formatDayShort(row.date)}: ${[
    pluralCount('sessions', row.sessions),
    pluralCount('unique', row.uniqueVisitors),
    pluralCount('registered', row.uniqueUsers),
  ].join(' · ')}`;
}

/** Every k-th day gets an x label so labels keep ≥ LABEL_SLOT_PX each. */
export function labelStep(days: number, plotWidth: number): number {
  const fit = Math.max(2, Math.floor(plotWidth / LABEL_SLOT_PX));
  return Math.max(1, Math.ceil(days / fit));
}

/** The container's content width, tracked; DEFAULT_WIDTH where it cannot be measured. */
function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = (w: number) => {
      if (w > 0) setWidth(Math.max(MIN_WIDTH, Math.round(w)));
    };
    update(el.clientWidth);
    const observer = new ResizeObserver((entries) => update(entries[0]?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

export function DailyChart({ days }: { days: readonly AnalyticsDailyRow[] }) {
  const [active, setActive] = useState<number | null>(null);
  const { ref: boxRef, width: W } = useMeasuredWidth();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const liveId = useId();

  const H = W < NARROW ? 200 : 220;
  const plotW = W - PAD_LEFT - PAD_RIGHT;
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const n = days.length;
  const ceiling = chartCeiling(Math.max(0, ...days.map((d) => Math.max(d.sessions, d.uniqueVisitors))));
  const slot = n > 0 ? plotW / n : plotW;
  const barW = Math.max(1, slot * 0.64);
  const y = (value: number) => PAD_TOP + plotH - (value / ceiling) * plotH;
  const xCenter = (i: number) => PAD_LEFT + slot * i + slot / 2;
  const labelEvery = labelStep(n, plotW);
  const linePoints = days.map((d, i) => `${xCenter(i)},${y(d.uniqueVisitors)}`).join(' ');
  const activeRow = active !== null ? days[active] : undefined;

  // Keep the tooltip inside the chart: centre it on the bar, then clamp to the
  // container's edges (first/last bars would otherwise push it off-screen).
  useLayoutEffect(() => {
    const tip = tooltipRef.current;
    if (!tip || active === null) return;
    tip.style.left = '0px';
    const tw = tip.offsetWidth;
    const left = Math.min(Math.max(0, xCenter(active) - tw / 2), Math.max(0, W - tw));
    tip.style.left = `${left}px`;
  });

  const move = (next: number) => setActive(Math.min(n - 1, Math.max(0, next)));
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (n === 0) return;
    const current = active ?? n - 1;
    const target =
      event.key === 'ArrowRight'
        ? current + 1
        : event.key === 'ArrowLeft'
          ? current - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? n - 1
              : null;
    if (target === null) return;
    event.preventDefault();
    move(target);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-4 text-[12px] text-cf-ink-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-cf-xs bg-cf-brand" aria-hidden="true" />
          {t('admin.analytics.chart.legendSessions')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-cf-accent" aria-hidden="true" />
          {t('admin.analytics.chart.legendUnique')}
        </span>
      </div>

      <div
        ref={boxRef}
        role="group"
        tabIndex={0}
        aria-label={t('admin.analytics.chart.ariaLabel')}
        aria-describedby={liveId}
        data-testid="daily-chart-focus"
        className="relative rounded-cf-md outline-none focus-visible:ring-2 focus-visible:ring-cf-ink"
        onFocus={() => {
          if (active === null && n > 0) setActive(n - 1);
        }}
        onBlur={() => setActive(null)}
        onKeyDown={onKeyDown}
        onMouseLeave={() => setActive(null)}
      >
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="block h-auto w-full"
          aria-hidden="true"
          data-testid="daily-chart"
        >
          {Array.from({ length: GRID_LINES + 1 }, (_, k) => {
            const value = (ceiling / GRID_LINES) * k;
            return (
              <g key={k}>
                <line
                  x1={PAD_LEFT}
                  x2={W - PAD_RIGHT}
                  y1={y(value)}
                  y2={y(value)}
                  stroke="var(--color-cf-line-soft)"
                  strokeWidth={1}
                />
                <text
                  x={PAD_LEFT - 6}
                  y={y(value) + 4}
                  textAnchor="end"
                  fontSize={FONT}
                  fill="var(--color-cf-ink-subtle)"
                  fontFamily="var(--font-mono)"
                >
                  {Math.round(value)}
                </text>
              </g>
            );
          })}

          {days.map((d, i) => (
            <rect
              key={d.date}
              data-date={d.date}
              x={xCenter(i) - barW / 2}
              y={y(d.sessions)}
              width={barW}
              height={Math.max(0, PAD_TOP + plotH - y(d.sessions))}
              rx={2}
              fill="var(--color-cf-brand)"
              opacity={active === null || active === i ? 1 : 0.55}
              onMouseEnter={() => setActive(i)}
            />
          ))}

          {n > 1 && (
            <polyline
              points={linePoints}
              fill="none"
              stroke="var(--color-cf-accent)"
              strokeWidth={2}
              strokeLinejoin="round"
              pointerEvents="none"
            />
          )}

          {days.map((d, i) =>
            i % labelEvery === 0 ? (
              <text
                key={d.date}
                x={xCenter(i)}
                y={H - 8}
                textAnchor="middle"
                fontSize={FONT}
                fill="var(--color-cf-ink-subtle)"
                fontFamily="var(--font-mono)"
              >
                {formatDayShort(d.date)}
              </text>
            ) : null,
          )}
        </svg>

        {activeRow && (
          <div
            ref={tooltipRef}
            role="tooltip"
            data-testid="daily-chart-tooltip"
            className="pointer-events-none absolute top-0 z-10 max-w-full rounded-cf-md border border-cf-line bg-cf-surface px-2.5 py-1.5 text-[12px] leading-[1.35] text-cf-ink shadow-sm sm:whitespace-nowrap"
          >
            {dayTooltip(activeRow)}
          </div>
        )}

        <p id={liveId} aria-live="polite" className="sr-only">
          {activeRow ? dayTooltip(activeRow) : ''}
        </p>
      </div>

      <details className="text-[13px]">
        <summary className="cursor-pointer text-cf-ink-muted hover:text-cf-ink">
          {t('admin.analytics.chart.table')}
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[420px] border-collapse font-mono text-[12px]">
            <thead>
              <tr className="text-left text-cf-ink-subtle">
                <th scope="col" className="py-1 pr-3 font-normal">
                  {t('admin.analytics.chart.date')}
                </th>
                <th scope="col" className="py-1 pr-3 text-right font-normal">
                  {t('admin.analytics.chart.sessions')}
                </th>
                <th scope="col" className="py-1 pr-3 text-right font-normal">
                  {t('admin.analytics.chart.uniqueVisitors')}
                </th>
                <th scope="col" className="py-1 pr-3 text-right font-normal">
                  {t('admin.analytics.chart.uniqueUsers')}
                </th>
                <th scope="col" className="py-1 text-right font-normal">
                  {t('admin.analytics.chart.pageViews')}
                </th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.date} className="border-t border-cf-line-faint">
                  <th scope="row" className="py-1 pr-3 text-left font-normal">
                    {formatDayShort(d.date)}
                  </th>
                  <td className="py-1 pr-3 text-right">{d.sessions}</td>
                  <td className="py-1 pr-3 text-right">{d.uniqueVisitors}</td>
                  <td className="py-1 pr-3 text-right">{d.uniqueUsers}</td>
                  <td className="py-1 text-right">{d.pageViews}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
